import { I18n } from '@aws-amplify/core';

import type { BeraReportV2, BeraSegmentAssignment } from '@/types/Bera';

export const PARTIAL_SUBTITLE = 'Select below the video files referring to the tasks that will be reevaluated';
export const NO_VIDEO_MESSAGE = 'To proceed, select at least one video for reevaluation';
export const NO_TLID_MESSAGE = 'You must identify the TLID in the selected files to proceed';
export const INCOMPLETE_TLID_MESSAGE =
	'Select a number of videos equal to the number of segments of the TLID to proceed';

export const VISIBLE_CHIPS = 2;

const SELECT_ALL_LABEL = 'Select all';
const DESELECT_ALL_LABEL = 'Deselect all';

const SINGLE_SEGMENT = 1;
const FIRST_ORDER = 1;
const NONE_ASSIGNED = 0;

export type SegmentLabel = {
	label: string;
	reportId: string;
};

export type SegmentLabels = Map<string, SegmentLabel>;

export type SegmentOption = {
	id: string;
	label: string;
	reportId: string;
	isDisabled: boolean;
};

export type SegmentChip = {
	segmentId: string;
	label: string;
};

export function getSelectAllLabel(isAllSelected: boolean): string {
	if (isAllSelected) {
		return DESELECT_ALL_LABEL;
	}

	return SELECT_ALL_LABEL;
}

function buildSegmentLabel(taskName: string, order: number, total: number): string {
	if (total === SINGLE_SEGMENT) {
		return taskName;
	}

	const label = `${taskName} (${order}º ${I18n.get('stretch')})`;

	return label;
}

export function buildSegmentLabels(reports: BeraReportV2[]): SegmentLabels {
	const entries = reports.flatMap((report) => {
		const segments = report.segments ?? [];
		const labelled = segments.map((segment, index): [string, SegmentLabel] => [
			segment.id,
			{ label: buildSegmentLabel(report.task_name, index + FIRST_ORDER, segments.length), reportId: report.id }
		]);

		return labelled;
	});
	const labels = new Map(entries);

	return labels;
}

function toSegmentIds(assignments: BeraSegmentAssignment[]): Set<string> {
	const ids = assignments.map((assignment) => assignment.segment_id);
	const result = new Set(ids);

	return result;
}

export function buildSegmentOptions(
	labels: SegmentLabels,
	assignments: BeraSegmentAssignment[],
	fileId: string
): SegmentOption[] {
	const foreignAssignments = assignments.filter((assignment) => assignment.file_id !== fileId);
	const takenSegmentIds = toSegmentIds(foreignAssignments);
	const entries = Array.from(labels.entries());
	const options = entries.map(([id, { label, reportId }]) => ({
		id,
		label,
		reportId,
		isDisabled: takenSegmentIds.has(id)
	}));

	return options;
}

export function buildFileChips(
	labels: SegmentLabels,
	assignments: BeraSegmentAssignment[],
	fileId: string
): SegmentChip[] {
	const fileAssignments = assignments.filter((assignment) => assignment.file_id === fileId);
	const ownedSegmentIds = toSegmentIds(fileAssignments);
	const entries = Array.from(labels.entries());
	const owned = entries.filter(([id]) => ownedSegmentIds.has(id));
	const chips = owned.map(([id, { label }]) => ({ segmentId: id, label }));

	return chips;
}

function isPartiallyAssigned(report: BeraReportV2, assignedSegmentIds: Set<string>): boolean {
	const segments = report.segments ?? [];
	const assigned = segments.filter((segment) => assignedSegmentIds.has(segment.id));
	const result = assigned.length > NONE_ASSIGNED && assigned.length < segments.length;

	return result;
}

interface FindValidationMessageParams {
	reports: BeraReportV2[];
	assignments: BeraSegmentAssignment[];
	selectedFileIds: string[];
}

export function findValidationMessage({
	reports,
	assignments,
	selectedFileIds
}: FindValidationMessageParams): string | null {
	if (selectedFileIds.length === NONE_ASSIGNED) {
		return NO_VIDEO_MESSAGE;
	}

	const assignedFileIds = assignments.map((assignment) => assignment.file_id);
	const identifiedFileIds = new Set(assignedFileIds);
	const hasFileWithoutStretch = selectedFileIds.some((fileId) => !identifiedFileIds.has(fileId));

	if (hasFileWithoutStretch) {
		return NO_TLID_MESSAGE;
	}

	const assignedSegmentIds = toSegmentIds(assignments);
	const hasIncompleteReport = reports.some((report) => isPartiallyAssigned(report, assignedSegmentIds));

	if (hasIncompleteReport) {
		return INCOMPLETE_TLID_MESSAGE;
	}

	return null;
}

export function buildPersistedAssignments(reports: BeraReportV2[]): BeraSegmentAssignment[] {
	const segments = reports.flatMap((report) => report.segments ?? []);
	const assignments = segments.flatMap((segment) => {
		if (!segment.source_file_id) {
			return [];
		}

		return [{ segment_id: segment.id, file_id: segment.source_file_id }];
	});

	return assignments;
}

export function listAssignedFileIds(assignments: BeraSegmentAssignment[]): string[] {
	const fileIds = assignments.map((assignment) => assignment.file_id);
	const uniqueFileIds = new Set(fileIds);
	const result = Array.from(uniqueFileIds);

	return result;
}

const FIRST_CHIP = 0;

export type VisibleChips = {
	visibleChips: SegmentChip[];
	overflowCount: number;
};

export function splitVisibleChips(chips: SegmentChip[]): VisibleChips {
	const visibleChips = chips.slice(FIRST_CHIP, VISIBLE_CHIPS);
	const overflowCount = chips.length - visibleChips.length;
	const result = { visibleChips, overflowCount };

	return result;
}
